<template>
  <v-col cols="12" lg="2" class="sticky-sidebar">
    <template v-if="!$vuetify.breakpoint.lgAndDown">
      <v-list
        class="pt-0 sticky-sidebar__item"
        :style="`${
          $vuetify.breakpoint.width < 1400 ? 'max-width: 163px' : null
        }`"
      >
        <active-hosting-plan-selector
          :items="services"
          :value="activeService"
          :returnObject="true"
          @change="changeService"
          itemText="name"
          itemValue="id"
          class="mb-6"
        />

        <v-subheader class="pl-0 p-4 mt-6 pb-3">{{ $t('heading.hosting.sidebar.manage.title') }}</v-subheader>
        <v-list-item-group
          color="primary"
          class="d-flex flex-row flex-lg-column justify-start"
        >
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/summary`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{ $t('heading.hosting.summary.title') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/domains`"
            v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_domains')"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{ $t('heading.hosting.domains.title') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/ftp`"
            v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_ftp')"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{ $t('heading.hosting.ftp.title') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/sftp`"
            v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_sftp')"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{ $t(`heading.hosting.${sftpPageType}.title`) }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/databases`"
            v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_mysql') || activeService.getUserPrivileges(activeService.privileges, 'hosting.phpmyadmin_sso')"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">
                {{ $t('heading.hosting.databases.title') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_cron')"
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/cron`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{ $t('heading.hosting.cron.title') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_file_manager')"
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/file-manager`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">{{ $t('heading.hosting.fileManager.title') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="
              activeService.dns_server_enabled
              && activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_dns')
              && activeService.plan_settings.dns_management_enabled
            "
            :active-class="'elevation-4--before'"
            :ripple="false"
            :to="`/hosting/dns`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">
                {{ $t('heading.hosting.dns.title') }}</v-list-item-title
              >
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>

        <v-subheader
          v-if="activeService.email_server_enabled && activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_email')"
          class="pl-0 p-4 mt-6 pb-3"
          >{{ $t('heading.hosting.sidebar.email.title') }}</v-subheader
        >
        <v-list-item-group
          v-if="activeService.email_server_enabled && activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_email')"
          color="primary"
          :active-class="'elevation-4--before'"
          class="d-flex flex-row flex-lg-column justify-start"
        >
          <v-list-item :ripple="false" :to="`/hosting/email-addresses`">
            <v-list-item-content>
              <v-list-item-title class="p-2">
                <span>{{ $t('heading.hosting.emailAddress.title') }}</span>
                <div
                  v-if="activeService.showEmailRecordConfiguration && !activeService.isEmailRecordsConfigured"
                  class="select-dot"
                />
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            :ripple="false"
            :active-class="'elevation-4--before'"
            :to="`/hosting/email-forwarders`"
          >
            <v-list-item-content>
              <v-list-item-title class="p-2">
                {{ $t('heading.hosting.emailForwarders.title') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </template>
    <template v-else>
      <active-hosting-plan-selector
        :items="services"
        :value="activeService"
        :returnObject="true"
        @change="changeService"
        itemText="name"
        itemValue="id"
        class="mt-2"
      />
      <v-tabs
        :class="$vuetify.breakpoint.mobile ? 'mt-4' : ''"
        center-active
        class="pt-0 d-flex flex-row flex-lg-column justify-start"
        style="overflow: hidden"
      >
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/summary`"
        >
          <span class="p-2">{{ $t('heading.hosting.summary.title') }}</span>
        </v-tab>
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/domains`"
          v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_domains')"
        >
          <span class="p-2">{{ $t('heading.hosting.domains.title') }}</span>
        </v-tab>
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/ftp`"
          v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_ftp')"
        >
          <span class="p-2">{{ $t('heading.hosting.ftp.title') }}</span>
        </v-tab>
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/sftp`"
          v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_sftp')"
        >
          <span class="p-2">{{ $t(`heading.hosting.${sftpPageType}.title`) }}</span>
        </v-tab>
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/databases`"
          v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_mysql') || activeService.getUserPrivileges(activeService.privileges, 'hosting.phpmyadmin_sso')"
        >
          <span class="p-2">{{ $t('heading.hosting.databases.title') }}</span>
        </v-tab>
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/cron`"
          v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_cron')"
        >
          <span class="p-2">{{ $t('heading.hosting.cron.title') }}</span>
        </v-tab>
        <v-tab
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/file-manager`"
          v-if="activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_file_manager')"
        >
          <span class="p-2">{{ $t('heading.hosting.fileManager.title') }}</span>
        </v-tab>
        <v-tab
          v-if="
            activeService.dns_server_enabled
            && activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_dns')
            && activeService.plan_settings.dns_management_enabled
          "
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/dns`"
        >
          <span class="p-2">{{ $t('heading.hosting.dns.title') }}</span>
        </v-tab>
        <v-tab
          v-if="activeService.email_server_enabled && activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_email')"
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/email-addresses`"
        >
          <span class="p-2">{{ $t('heading.hosting.emailAddress.title') }}</span>
          <div
            v-if="activeService.showEmailRecordConfiguration && !activeService.isEmailRecordsConfigured"
            class="select-dot"
          />
        </v-tab>
        <v-tab
          v-if="activeService.email_server_enabled && activeService.getUserPrivileges(activeService.privileges, 'hosting.manage_email')"
          :active-class="'elevation-2'"
          :ripple="false"
          :to="`/hosting/email-forwarders`"
        >
          <span class="p-2">{{ $t('heading.hosting.emailForwarders.title') }}</span>
        </v-tab>
      </v-tabs>
    </template>
  </v-col>
</template>

<script>
import ActiveHostingPlanSelector from "./ActiveHostingPlanSelector.vue";

export default {
  components: {
    ActiveHostingPlanSelector,
  },
  props: {
    services: {
      type: Array,
      default: () => {
        [];
      },
    },
    activeService: {
      type: Object,
    },
  },
  methods: {
    changeService: function (item) {
      this.$emit("update:activeService", item);
    },
  },
  computed: {
    sftpPageType() {
      if (
        this.activeService.plan_settings.default_ssh_access_enabled
        || this.activeService.plan_settings.allow_user_enable_ssh_access
      ) {
        return 'ssh';
      }
      return 'sftp';
    }
  }
};
</script>

<style lang="scss" scoped>
.v-subheader {
  align-content: flex-start;
  height: 30px;
  color: var(--v-gray-lighten1);
}
.v-item-group {
  margin-bottom: 24px;
}

.v-list {
  .v-list-item {
    padding-left: 0px;
    transition: padding-left 0.3s ease;
    .v-list-item__content {
      position: relative;
      z-index: 1;
    }
    .v-list-item__title {
      font-weight: $font-weight-semibold;
      color: map-get($text, header);
      transition: color 0.24s ease, font-weight 0.24s ease;
    }
    &::before {
      z-index: 0;
      background: #fff;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    &:hover {
      padding-left: 12px;
      .v-list-item__title {
        color: map-get($primary, base);
      }
    }
    &--active {
      padding-left: 12px;
      border-radius: 4px;
      .v-list-item__title {
        font-weight: $font-weight-bold;
        color: map-get($primary, darken1);
      }
      &::before {
        border-radius: 4px;
        opacity: 1 !important;
      }
    }
  }

  @media (max-width: map-get($grid-breakpoints, md)) {
    .v-list-item-group {
      justify-content: flex-start;
    }
    .v-list-item {
      padding: 0 12px 0 12px !important;
      text-align: center;
      min-width: unset !important;
    }
  }
}

.sticky-sidebar {
  position: relative;
}

.sticky-sidebar__item {
  position: sticky;
  padding-left: 0px !important;
  top: 120px;
  left: 0;
  background-color: var(--v-body-base);
  .v-input {
    background-color: var(--v-panel-base);
  }
}

.v-tabs::v-deep {
  padding: 8px 0 !important;
  margin: 0 -24px;
  overflow: hidden !important;
  width: 100vw;
  .v-item-group {
    background-color: transparent;
  }
  .v-slide-group__wrapper {
    contain: none !important;
    overflow: visible !important;

    max-width: 100%;
  }
  .v-tabs-slider-wrapper {
    display: none;
  }
  .v-tab {
    position: relative;
    overflow: visible !important;
    padding: 0 16px;
    border-radius: 4px;
    box-shadow: none;
    transition: opacity 0.3s ease;
    margin-left: 4px;
    margin-right: 4px;
    background: transparent;
    span {
      font-size: $font-size-base;
      line-height: $line-height-base;
      color: map-get($text, heading);
      font-weight: $font-weight-semibold;
    }
    &:not(:first-child) {
      margin-left: 0 !important;
    }

    &--active {
      background: #fff;

      span {
        color: map-get($primary, darken1);
        font-weight: $font-weight-bold;
      }
    }
  }
}

.v-application--is-rtl{
    .v-list {
      .v-list-item {
        padding-right: 0px;
        transition: padding-right 0.3s ease;
        &:hover{
            padding-right: 12px;
        }
        &--active{
            padding-right: 12px;
        }
      }
    }
}

.select{
  &-dot{
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 8px;
    margin-left: 8px;
    margin-bottom: 1px;
    background: var(--v-error-base);
  }
}
</style>
